import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from "react-router-dom";
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader } from "../ui";
import { useEFrameData } from "./hooks/useEFrameData";
import { getPlanOwnerName } from "../../utils/planOwnerLabels";
import type { AppDocument } from '../../types/document';
import {
  NewDogIcon,
  NewCatIcon,
  NewBirdIcon,
  NewFishIcon,
  NewReptilesIcon,
  NewOtherPetsIcon,
  NewChildIcon,
  NewParentsIcon,
  NewSiblingsIcon,
  NewSpousesIcon,
  NewOtherDependentsIcon,
} from '../ui/Icons';

interface Pet {
  id: string;
  name: string;
  type: 'dog' | 'cat' | 'bird' | 'fish' | 'reptile' | 'other' | '';
  breed: string;
  age: string;
  specialNeeds: string;
  veterinarian: string;
  veterinarianContact: string;
  preferredCaregiver: string;
  caregiverContact: string;
  careInstructions: string;
  diet: string;
  medications: string;
  emergencyContact: string;
  emergencyContactPhone: string;
  documents: AppDocument[];
}

interface Dependant {
  id: string;
  name: string;
  relationship: 'child' | 'parent' | 'sibling' | 'spouse' | 'other' | '';
  age: string;
  specialNeeds: string;
  medicalConditions: string;
  medications: string;
  preferredGuardian: string;
  guardianContact: string;
  emergencyContact: string;
  emergencyContactPhone: string;
  careInstructions: string;
  documents: AppDocument[];
}

interface PetsAndDependantsData {
  pets: Pet[];
  dependants: Dependant[];
}

const petCategories = [
  { key: 'dog' as const, Icon: NewDogIcon },
  { key: 'cat' as const, Icon: NewCatIcon },
  { key: 'bird' as const, Icon: NewBirdIcon },
  { key: 'fish' as const, Icon: NewFishIcon },
  { key: 'reptile' as const, Icon: NewReptilesIcon },
  { key: 'other' as const, Icon: NewOtherPetsIcon },
];

const dependentCategories = [
  { key: 'child' as const, Icon: NewChildIcon },
  { key: 'parent' as const, Icon: NewParentsIcon },
  { key: 'sibling' as const, Icon: NewSiblingsIcon },
  { key: 'spouse' as const, Icon: NewSpousesIcon },
  { key: 'other' as const, Icon: NewOtherDependentsIcon },
];

const AdultDependentsIcon = ({ className = 'w-6 h-6 text-cyan-700' }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="9.5" cy="7" r="4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20 21v-2a4 4 0 0 0-3-3.87" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  if (!Array.isArray(docs)) return [];

  return docs
    .filter((doc): doc is Record<string, unknown> => !!doc && typeof doc === 'object')
    .map((doc) => ({
      id: typeof doc.id === 'string' ? doc.id : '',
      name: typeof doc.name === 'string' ? doc.name : 'Untitled document',
      mimeType: typeof doc.mimeType === 'string' ? doc.mimeType : undefined,
      size: typeof doc.size === 'number' ? doc.size : undefined,
      createdAt: typeof doc.createdAt === 'string' ? doc.createdAt : undefined,
    }))
    .filter((doc) => !!doc.id);
};

const normalizeData = (raw: unknown): PetsAndDependantsData => {
  const safe = raw && typeof raw === 'object' ? (raw as Partial<PetsAndDependantsData>) : {};

  const pets = Array.isArray(safe.pets)
    ? safe.pets.map((pet) => ({
        ...(pet as Pet),
        documents: normalizeDocuments((pet as Partial<Pet>).documents),
      }))
    : [];

  const dependants = Array.isArray(safe.dependants)
    ? safe.dependants.map((dependant) => ({
        ...(dependant as Dependant),
        documents: normalizeDocuments((dependant as Partial<Dependant>).documents),
      }))
    : [];

  return { pets, dependants };
};

// Executor read-only: never fall back to browser localStorage backup keys.
const loadFromLocalStorage = (): PetsAndDependantsData => ({ pets: [], dependants: [] });

const getPetIcon = (type: Pet['type'], className = 'w-6 h-6 text-cyan-700') => {
  const match = petCategories.find((category) => category.key === type);
  const Icon = match?.Icon || NewOtherPetsIcon;
  return <Icon className={className} />;
};

const getDependentIcon = (
  relationship: Dependant['relationship'],
  className = 'w-6 h-6 text-cyan-700'
) => {
  const match = dependentCategories.find((category) => category.key === relationship);
  const Icon = match?.Icon || NewOtherDependentsIcon;
  return <Icon className={className} />;
};

const getDocumentIcon = (doc: AppDocument, className = 'w-4 h-4 text-cyan-700') => {
  const fileName = doc.name.toLowerCase();
  const fileType = (doc.mimeType || '').toLowerCase();

  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 3v5h5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {fileType.includes('pdf') || fileName.endsWith('.pdf') ? (
        <path d="M8 13h8M8 17h6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M8 13h8M8 17h8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
};

const DocumentList = ({ ownerId, documents }: { ownerId: string; documents?: AppDocument[] }) => {
  if (!documents || documents.length === 0) return null;

  return (
    <div className="mt-3 space-y-2 min-w-0 overflow-hidden">
      <div className="text-sm text-cyan-700 font-medium">
        {documents.length} document(s) attached
      </div>

      <div className="space-y-2 min-w-0">
        {documents.map((doc, index) => (
          <div
            key={`${ownerId}-${doc.id}-${index}`}
            className="flex items-start gap-2 rounded-md border border-stone-200 bg-stone-50 px-3 py-2 min-w-0"
          >
            {getDocumentIcon(doc, "w-4 h-4 text-cyan-700 shrink-0 mt-0.5")}

            <span
              className="text-sm font-medium text-stone-800 break-words whitespace-normal min-w-0"
              title={doc.name}
            >
              {doc.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const EFrame146: React.FC = () => {
  const { estateId } = useParams<{ estateId: string }>();
  const { session } = useEFrameData(estateId ?? null);
  const planOwnerName = getPlanOwnerName(session.name);

  const [data, setData] = useState<PetsAndDependantsData>({ pets: [], dependants: [] });
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (session?.taskProgress?.pets?.answers?.petsAndDependants) {
      setData(normalizeData(session.taskProgress.pets.answers.petsAndDependants));
      setLoading(false);
      return;
    }

    setData(loadFromLocalStorage());
    setLoading(false);
  }, [session?.taskProgress?.pets]);

  const children = useMemo(
    () => data.dependants.filter((dependant) => dependant.relationship === 'child'),
    [data.dependants]
  );

  const adultDependants = useMemo(
    () => data.dependants.filter((dependant) => dependant.relationship && dependant.relationship !== 'child'),
    [data.dependants]
  );

  const statCards = [
    {
      label: 'Children',
      value: children.length,
      icon: <NewChildIcon className="w-6 h-6 text-cyan-700" />,
    },
    {
      label: 'Adult Dependents',
      value: adultDependants.length,
      icon: <AdultDependentsIcon className="w-6 h-6 text-cyan-700" />,
    },
    {
      label: 'Pets',
      value: data.pets.length,
      icon: <NewDogIcon className="w-6 h-6 text-cyan-700" />,
    },
  ];

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="w-full max-w-[835px] min-w-0 overflow-x-hidden">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 min-w-0">
            <div className="flex-1 min-w-0">
              <ResponsiveHeader title={`${planOwnerName}'s Dependents & Pets`} />
            </div>
          </div>

          <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed break-words">
            Explore the documented care plans for {planOwnerName}'s dependents and pets. 
            Familiarizing yourself with these arrangements helps you quickly coordinate with chosen guardians and caregivers.
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-5 min-w-0">
            {statCards.map((card) => (
              <div
                key={card.label}
                className="bg-white rounded-md border border-stone-200 p-6 min-w-0 overflow-hidden"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="shrink-0">{card.icon}</div>

                  <div className="flex flex-col min-w-0">
                    <div className="font-sans text-base text-stone-600 break-words">
                      {card.label}
                    </div>

                    <div className="font-sans text-3xl font-medium text-stone-800 leading-none mt-4 break-words">
                      {loading ? '—' : card.value}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Children */}
          <div className="mt-7 min-w-0">
            <div className="flex items-center gap-3 mb-3 min-w-0">
              <h2 className="font-sans text-xl font-medium text-stone-800 break-words">
                Children
              </h2>
            </div>

            <div className="space-y-3 min-w-0">
              {loading ? (
                <div className="text-center py-12 text-stone-500">
                  <div className="inline-block w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin mb-2" />
                  <p className="font-sans text-sm">Loading children...</p>
                </div>
              ) : children.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-stone-300 rounded-lg">
                  <p className="font-sans text-stone-500 text-sm">
                    No children added yet.
                  </p>
                </div>
              ) : (
                children.map((child) => {
                  const isExpanded = !!expandedIds[`child-${child.id}`];
                  const metadataParts = [
                    child.age || null,
                    child.preferredGuardian ? `Guardian: ${child.preferredGuardian}` : null,
                  ].filter(Boolean);

                  return (
                    <div
                      key={child.id}
                      className="flex flex-col gap-4 bg-white rounded-lg border border-stone-200 p-4 sm:p-6 min-w-0 overflow-hidden"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4 min-w-0">
                        <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 flex-1 min-w-0 overflow-hidden">
                          {getDependentIcon(child.relationship, 'w-6 h-6 text-cyan-700 flex-shrink-0 mt-0.5')}

                          <div className="flex-1 min-w-0 w-full overflow-hidden">
                            <div className="font-sans text-lg font-medium text-stone-800 break-words">
                              {child.name}
                            </div>

                            <div className="font-sans text-base text-stone-600 mt-1 break-words">
                              Child
                            </div>

                            {metadataParts.length > 0 && (
                              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-y-1 sm:gap-x-5 font-sans text-base text-stone-600 mt-1 min-w-0">
                                {metadataParts.map((part, index) => (
                                  <span key={index} className="break-words min-w-0">
                                    {part}
                                  </span>
                                ))}
                              </div>
                            )}

                            {child.specialNeeds && (
                              <div className="font-sans text-sm text-stone-500 mt-2 break-words leading-relaxed min-w-0">
                                {child.specialNeeds}
                              </div>
                            )}

                            <DocumentList ownerId={child.id} documents={child.documents} />

                            {isExpanded && (
                              <div className="mt-3 space-y-2 min-w-0 overflow-hidden border-t border-stone-100 pt-3">
                                {child.guardianContact && (
                                  <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Guardian Contact:</span> {child.guardianContact}
                                  </div>
                                )}

                                {child.emergencyContact && (
                                  <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Emergency Contact:</span> {child.emergencyContact}
                                  </div>
                                )}

                                {child.emergencyContactPhone && (
                                  <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Emergency Phone:</span> {child.emergencyContactPhone}
                                  </div>
                                )}

                                {child.medicalConditions && (
                                  <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Medical Conditions:</span> {child.medicalConditions}
                                  </div>
                                )}

                                {child.medications && (
                                  <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Medications:</span> {child.medications}
                                  </div>
                                )}

                                {child.careInstructions && (
                                  <div className="font-sans text-sm text-stone-500 whitespace-pre-wrap break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Care Instructions:</span>
                                    <br />
                                    {child.careInstructions}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 shrink-0 w-full sm:w-auto">
                          <button
                            onClick={() => toggleExpanded(`child-${child.id}`)}
                            className="text-stone-700 hover:text-stone-900 px-3 py-1 rounded border border-stone-300 hover:border-stone-500 font-sans text-sm font-medium transition-colors"
                          >
                            {isExpanded ? 'Show Less' : 'Show More'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Adult Dependents */}
          <div className="mt-7 min-w-0">
            <div className="flex items-center gap-3 mb-3 min-w-0">
              <h2 className="font-sans text-xl font-medium text-stone-800 break-words">
                Adult Dependents
              </h2>
            </div>

            <div className="space-y-3 min-w-0">
              {loading ? (
                <div className="text-center py-12 text-stone-500">
                  <div className="inline-block w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin mb-2" />
                  <p className="font-sans text-sm">Loading adult dependents...</p>
                </div>
              ) : adultDependants.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-stone-300 rounded-lg">
                  <p className="font-sans text-stone-500 text-sm">
                    No adult dependents added yet.
                  </p>
                </div>
              ) : (
                adultDependants.map((dependant) => {
                  const isExpanded = !!expandedIds[`adult-${dependant.id}`];
                  const relationshipLabel =
                    dependant.relationship.charAt(0).toUpperCase() + dependant.relationship.slice(1);

                  const metadataParts = [
                    dependant.age || null,
                    dependant.preferredGuardian ? `Guardian: ${dependant.preferredGuardian}` : null,
                  ].filter(Boolean);

                  return (
                    <div
                      key={dependant.id}
                      className="flex flex-col gap-4 bg-white rounded-lg border border-stone-200 p-4 sm:p-6 min-w-0 overflow-hidden"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4 min-w-0">
                        <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 flex-1 min-w-0 overflow-hidden">
                          {getDependentIcon(dependant.relationship, 'w-6 h-6 text-cyan-700 flex-shrink-0 mt-0.5')}

                          <div className="flex-1 min-w-0 w-full overflow-hidden">
                            <div className="font-sans text-lg font-medium text-stone-800 break-words">
                              {dependant.name}
                            </div>

                            <div className="font-sans text-base text-stone-600 mt-1 break-words">
                              {relationshipLabel}
                            </div>

                            {metadataParts.length > 0 && (
                              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-y-1 sm:gap-x-5 font-sans text-base text-stone-600 mt-1 min-w-0">
                                {metadataParts.map((part, index) => (
                                  <span key={index} className="break-words min-w-0">
                                    {part}
                                  </span>
                                ))}
                              </div>
                            )}

                            {dependant.specialNeeds && (
                              <div className="font-sans text-sm text-stone-500 mt-2 break-words leading-relaxed min-w-0">
                                {dependant.specialNeeds}
                              </div>
                            )}

                            <DocumentList ownerId={dependant.id} documents={dependant.documents} />

                            {isExpanded && (
                              <div className="mt-3 space-y-2 min-w-0 overflow-hidden border-t border-stone-100 pt-3">
                                {dependant.guardianContact && (
                                  <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Guardian Contact:</span> {dependant.guardianContact}
                                  </div>
                                )}

                                {dependant.emergencyContact && (
                                  <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Emergency Contact:</span> {dependant.emergencyContact}
                                  </div>
                                )}

                                {dependant.emergencyContactPhone && (
                                  <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Emergency Phone:</span> {dependant.emergencyContactPhone}
                                  </div>
                                )}

                                {dependant.medicalConditions && (
                                  <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Medical Conditions:</span> {dependant.medicalConditions}
                                  </div>
                                )}

                                {dependant.medications && (
                                  <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Medications:</span> {dependant.medications}
                                  </div>
                                )}

                                {dependant.careInstructions && (
                                  <div className="font-sans text-sm text-stone-500 whitespace-pre-wrap break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Care Instructions:</span>
                                    <br />
                                    {dependant.careInstructions}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 shrink-0 w-full sm:w-auto">
                          <button
                            onClick={() => toggleExpanded(`adult-${dependant.id}`)}
                            className="text-stone-700 hover:text-stone-900 px-3 py-1 rounded border border-stone-300 hover:border-stone-500 font-sans text-sm font-medium transition-colors"
                          >
                            {isExpanded ? 'Show Less' : 'Show More'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Pets */}
          <div className="mt-7 min-w-0">
            <div className="flex items-center gap-3 mb-3 min-w-0">
              <h2 className="font-sans text-xl font-medium text-stone-800 break-words">
                Pets
              </h2>
            </div>

            <div className="space-y-3 min-w-0">
              {loading ? (
                <div className="text-center py-12 text-stone-500">
                  <div className="inline-block w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin mb-2" />
                  <p className="font-sans text-sm">Loading pets...</p>
                </div>
              ) : data.pets.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-stone-300 rounded-lg">
                  <p className="font-sans text-stone-500 text-sm">
                    No pets added yet.
                  </p>
                </div>
              ) : (
                data.pets.map((pet) => {
                  const isExpanded = !!expandedIds[`pet-${pet.id}`];
                  const petTypeLabel =
                    pet.type ? pet.type.charAt(0).toUpperCase() + pet.type.slice(1) : 'Pet';

                  const metadataParts = [
                    pet.age || null,
                    pet.veterinarian ? `Vet: ${pet.veterinarian}` : null,
                    pet.preferredCaregiver ? `Caregiver: ${pet.preferredCaregiver}` : null,
                  ].filter(Boolean);

                  return (
                    <div
                      key={pet.id}
                      className="flex flex-col gap-4 bg-white rounded-lg border border-stone-200 p-4 sm:p-6 min-w-0 overflow-hidden"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4 min-w-0">
                        <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 flex-1 min-w-0 overflow-hidden">
                          {getPetIcon(pet.type, 'w-6 h-6 text-cyan-700 flex-shrink-0 mt-0.5')}

                          <div className="flex-1 min-w-0 w-full overflow-hidden">
                            <div className="font-sans text-lg font-medium text-stone-800 break-words">
                              {pet.name}
                            </div>

                            <div className="font-sans text-base text-stone-600 mt-1 break-words">
                              {petTypeLabel}
                            </div>

                            {metadataParts.length > 0 && (
                              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-y-1 sm:gap-x-5 font-sans text-base text-stone-600 mt-1 min-w-0">
                                {metadataParts.map((part, index) => (
                                  <span key={index} className="break-words min-w-0">
                                    {part}
                                  </span>
                                ))}
                              </div>
                            )}

                            {pet.specialNeeds && (
                              <div className="font-sans text-sm text-stone-500 mt-2 break-words leading-relaxed min-w-0">
                                {pet.specialNeeds}
                              </div>
                            )}

                            <DocumentList ownerId={pet.id} documents={pet.documents} />

                            {isExpanded && (
                              <div className="mt-3 space-y-2 min-w-0 overflow-hidden border-t border-stone-100 pt-3">
                                {pet.veterinarianContact && (
                                  <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Vet Contact:</span> {pet.veterinarianContact}
                                  </div>
                                )}

                                {pet.caregiverContact && (
                                  <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Caregiver Contact:</span> {pet.caregiverContact}
                                  </div>
                                )}

                                {pet.diet && (
                                  <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Diet:</span> {pet.diet}
                                  </div>
                                )}

                                {pet.medications && (
                                  <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Medications:</span> {pet.medications}
                                  </div>
                                )}

                                {pet.careInstructions && (
                                  <div className="font-sans text-sm text-stone-500 whitespace-pre-wrap break-words leading-relaxed min-w-0">
                                    <span className="font-medium text-stone-700">Care Instructions:</span>
                                    <br />
                                    {pet.careInstructions}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 shrink-0 w-full sm:w-auto">
                          <button
                            onClick={() => toggleExpanded(`pet-${pet.id}`)}
                            className="text-stone-700 hover:text-stone-900 px-3 py-1 rounded border border-stone-300 hover:border-stone-500 font-sans text-sm font-medium transition-colors"
                          >
                            {isExpanded ? 'Show Less' : 'Show More'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default EFrame146;